import {
  createContext,
  createElement,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./auth/AuthProvider";

// Cross-device "Continue Thinking" state. Replaces the old per-browser
// localStorage (burrow:pinned-continue / surfaced-at / resurface-frequency)
// with the `resurface_state` table + profiles.resurface_frequency, so pins,
// cooldowns, cadence, and manual-resurface flags follow the user between phone
// and desktop.
//
// Exposed through a single <ResurfaceProvider> so the Thinking Space deck, the
// card detail sheet, Home, and Settings all read/write the SAME state instead
// of each holding its own drifting copy.

export type ResurfaceFrequency = "daily" | "weekly" | "biweekly";

export const COOLDOWN_MS: Record<ResurfaceFrequency, number> = {
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  biweekly: 14 * 24 * 60 * 60 * 1000,
};

export interface ResurfaceApi {
  /** True once the initial fetch has resolved. */
  loaded: boolean;
  pinnedIds: Set<string>;
  togglePin: (thoughtId: string) => void;
  /** Thoughts the user has explicitly flagged to resurface (non-loose cards). */
  resurfacedIds: Set<string>;
  /**
   * Flag/unflag a card for manual resurfacing. Turning it on also clears any
   * cooldown so the card reappears in the deck right away.
   */
  setResurface: (thoughtId: string, on: boolean) => void;
  /** thoughtId → epoch ms of when it was last surfaced. */
  surfacedAt: Record<string, number>;
  /** Mark cards as just-surfaced (pushes them into cooldown). */
  recordSurfaced: (thoughtIds: string[]) => void;
  /** Set a single card aside until its cooldown elapses. */
  snooze: (thoughtId: string) => void;
  /**
   * Reverse a `snooze` — clears the cooldown stamp so the card is eligible
   * again immediately. Distinct from `setResurface(id, true)`, which would also
   * flag the card for manual resurfacing; undoing a snooze must not invent a
   * flag the card never had.
   */
  unsnooze: (thoughtId: string) => void;
  frequency: ResurfaceFrequency;
  setFrequency: (f: ResurfaceFrequency) => void;
  cooldownMs: number;
}

const ResurfaceContext = createContext<ResurfaceApi | null>(null);

function useResurfaceState(): ResurfaceApi {
  const auth = useAuth();
  const userId = auth.status === "signed_in" ? auth.user.id : null;

  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [resurfacedIds, setResurfacedIds] = useState<Set<string>>(new Set());
  const [surfacedAt, setSurfacedAt] = useState<Record<string, number>>({});
  const [frequency, setFrequencyState] = useState<ResurfaceFrequency>("daily");
  const [loaded, setLoaded] = useState(false);

  // Initial load for the signed-in user.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [rowsRes, profileRes] = await Promise.all([
        supabase
          .from("resurface_state")
          .select("thought_id,surfaced_at,pinned,resurface")
          .eq("user_id", userId),
        supabase
          .from("profiles")
          .select("resurface_frequency")
          .eq("id", userId)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const pins = new Set<string>();
      const resurfaced = new Set<string>();
      const surfaced: Record<string, number> = {};
      for (const r of (rowsRes.data ?? []) as {
        thought_id: string;
        surfaced_at: string | null;
        pinned: boolean;
        resurface: boolean;
      }[]) {
        if (r.pinned) pins.add(r.thought_id);
        if (r.resurface) resurfaced.add(r.thought_id);
        if (r.surfaced_at) surfaced[r.thought_id] = new Date(r.surfaced_at).getTime();
      }
      setPinnedIds(pins);
      setResurfacedIds(resurfaced);
      setSurfacedAt(surfaced);

      const profileRow = profileRes.data as { resurface_frequency?: string } | null;
      const freq = profileRow?.resurface_frequency;
      if (freq === "daily" || freq === "weekly" || freq === "biweekly") {
        setFrequencyState(freq);
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const togglePin = useCallback(
    (thoughtId: string) => {
      if (!userId) return;
      setPinnedIds((prev) => {
        const next = new Set(prev);
        const willPin = !next.has(thoughtId);
        if (willPin) next.add(thoughtId);
        else next.delete(thoughtId);
        // Fire-and-forget; only the `pinned` column is written so an existing
        // surfaced_at is preserved on conflict.
        void supabase
          .from("resurface_state")
          .upsert(
            {
              user_id: userId,
              thought_id: thoughtId,
              pinned: willPin,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id,thought_id" }
          );
        return next;
      });
    },
    [userId]
  );

  const setResurface = useCallback(
    (thoughtId: string, on: boolean) => {
      if (!userId) return;
      setResurfacedIds((prev) => {
        const next = new Set(prev);
        if (on) next.add(thoughtId);
        else next.delete(thoughtId);
        return next;
      });
      // Turning resurfacing ON also clears the cooldown so the card shows in the
      // deck immediately rather than waiting out a leftover surfaced_at.
      if (on) {
        setSurfacedAt((prev) => {
          if (!(thoughtId in prev)) return prev;
          const next = { ...prev };
          delete next[thoughtId];
          return next;
        });
      }
      void supabase.from("resurface_state").upsert(
        {
          user_id: userId,
          thought_id: thoughtId,
          resurface: on,
          ...(on ? { surfaced_at: null } : {}),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,thought_id" }
      );
    },
    [userId]
  );

  const recordSurfaced = useCallback(
    (thoughtIds: string[]) => {
      if (!userId || thoughtIds.length === 0) return;
      const now = Date.now();
      setSurfacedAt((prev) => {
        const next = { ...prev };
        for (const id of thoughtIds) next[id] = now;
        return next;
      });
      const iso = new Date(now).toISOString();
      // Only surfaced_at is written, so a card's pin/resurface state is preserved.
      void supabase
        .from("resurface_state")
        .upsert(
          thoughtIds.map((id) => ({
            user_id: userId,
            thought_id: id,
            surfaced_at: iso,
            updated_at: iso,
          })),
          { onConflict: "user_id,thought_id" }
        );
    },
    [userId]
  );

  const snooze = useCallback((thoughtId: string) => recordSurfaced([thoughtId]), [recordSurfaced]);

  const unsnooze = useCallback(
    (thoughtId: string) => {
      if (!userId) return;
      setSurfacedAt((prev) => {
        if (!(thoughtId in prev)) return prev;
        const next = { ...prev };
        delete next[thoughtId];
        return next;
      });
      void supabase.from("resurface_state").upsert(
        {
          user_id: userId,
          thought_id: thoughtId,
          surfaced_at: null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,thought_id" }
      );
    },
    [userId]
  );

  const setFrequency = useCallback(
    (f: ResurfaceFrequency) => {
      setFrequencyState(f);
      if (userId) {
        void supabase.from("profiles").update({ resurface_frequency: f }).eq("id", userId);
      }
    },
    [userId]
  );

  return {
    loaded,
    pinnedIds,
    togglePin,
    resurfacedIds,
    setResurface,
    surfacedAt,
    recordSurfaced,
    snooze,
    unsnooze,
    frequency,
    setFrequency,
    cooldownMs: COOLDOWN_MS[frequency] ?? COOLDOWN_MS.daily,
  };
}

export function ResurfaceProvider({ children }: { children: ReactNode }) {
  const value = useResurfaceState();
  return createElement(ResurfaceContext.Provider, { value }, children);
}

export function useResurface(): ResurfaceApi {
  const ctx = useContext(ResurfaceContext);
  if (!ctx) throw new Error("useResurface must be used within a ResurfaceProvider");
  return ctx;
}
